/**
 * Training workspace.
 *
 * Centers the org's primary training session and lets new knowledge roll into it over time.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  GraduationCap,
  Globe,
  Copy,
  ExternalLink,
  Loader2,
  Trash2,
  Users,
} from 'lucide-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useInterviewerSetup } from '@/hooks/useInterviewerSetup';
import type { Interviewer } from '@/lib/api/onboarding';
import {
  ContentSelector,
  KnowledgeAssetSelector,
} from '@/components/onboarding/KnowledgeSourceSelectors';

// ========================================
// TRAINING SESSION CARD
// ========================================

const InterviewerCard: React.FC<{
  interviewer: Interviewer;
  onCopy: (interviewer: Interviewer) => void;
  onDelete?: (id: string) => void;
  isDeleting?: boolean;
  title?: string;
  subtitle?: string;
}> = ({ interviewer, onCopy, onDelete, isDeleting = false, title, subtitle }) => {
  const navigate = useNavigate();
  const link = interviewer.interview_link || `${window.location.origin}/interview/${interviewer.token}`;

  return (
    <Card
      className="cursor-pointer hover:border-primary/40 transition-colors"
      onClick={() => navigate(`/training/${interviewer.id}`)}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-primary shrink-0" />
              <h3 className="font-medium text-sm truncate">
                {title || interviewer.name || 'Training Workspace'}
              </h3>
              <Badge
                variant={interviewer.processing_status === 'completed' ? 'default' : 'secondary'}
                className="text-xs shrink-0"
              >
                {interviewer.processing_status === 'completed' ? 'Ready' : interviewer.processing_status}
              </Badge>
              {interviewer.is_primary && (
                <Badge variant="outline" className="text-xs shrink-0">
                  Primary
                </Badge>
              )}
            </div>

            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}

            {interviewer.website_url && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Globe className="h-3 w-3" />
                <span className="truncate">{interviewer.website_url}</span>
              </div>
            )}

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {interviewer.session_count} session{interviewer.session_count !== 1 ? 's' : ''}
              </span>
              <span>{new Date(interviewer.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onCopy(interviewer)}
              title="Copy link"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => window.open(link, '_blank')}
              title="Open link"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                onClick={() => onDelete(interviewer.id)}
                disabled={isDeleting}
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ========================================
// MAIN PAGE
// ========================================

export const InterviewerLinksContent: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id || '';

  const {
    interviewers,
    primaryInterviewer,
    isLoading,
    isCreating,
    isConsolidating,
    isDeleting,
    createInterviewer,
    consolidateInterviewers,
    deleteInterviewer,
    copyLink,
    uploadFile,
  } = useInterviewerSetup(orgId);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [knowledgeBaseUrl, setKnowledgeBaseUrl] = useState('');
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
  const [selectedKnowledgeAssetIds, setSelectedKnowledgeAssetIds] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const hasSources =
    !!websiteUrl.trim() ||
    !!knowledgeBaseUrl.trim() ||
    selectedVideoIds.length > 0 ||
    selectedKnowledgeAssetIds.length > 0 ||
    selectedFiles.length > 0;

  const handleCreate = useCallback(async () => {
    setIsUploadingFiles(true);

    let fileUploads;
    if (selectedFiles.length > 0) {
      const uploaded = await Promise.all(selectedFiles.map((file) => uploadFile(file)));
      if (uploaded.some((file) => !file)) {
        setIsUploadingFiles(false);
        return;
      }
      fileUploads = uploaded.filter(Boolean);
    }

    setIsUploadingFiles(false);

    const result = await createInterviewer(
      websiteUrl.trim() || undefined,
      knowledgeBaseUrl.trim() || undefined,
      fileUploads,
      name.trim() || undefined,
      selectedVideoIds.length > 0 ? selectedVideoIds : undefined,
      selectedKnowledgeAssetIds.length > 0 ? selectedKnowledgeAssetIds : undefined,
    );
    if (result) {
      setName('');
      setWebsiteUrl('');
      setKnowledgeBaseUrl('');
      setSelectedVideoIds([]);
      setSelectedKnowledgeAssetIds([]);
      setSelectedFiles([]);
      navigate(`/training/${result.id}`);
    }
  }, [
    createInterviewer,
    knowledgeBaseUrl,
    navigate,
    name,
    selectedFiles,
    selectedKnowledgeAssetIds,
    selectedVideoIds,
    uploadFile,
    websiteUrl,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleCreate();
      }
    },
    [handleCreate]
  );

  const legacyInterviewers = interviewers.filter(
    (interviewer) => !primaryInterviewer || interviewer.id !== primaryInterviewer.id
  );

  const handleConsolidateLegacy = useCallback(async () => {
    if (!primaryInterviewer || legacyInterviewers.length === 0 || isConsolidating) {
      return;
    }

    const confirmed = window.confirm(
      `Merge ${legacyInterviewers.length} legacy training record${legacyInterviewers.length === 1 ? '' : 's'} into the primary training workspace? This will archive the legacy records from the main list.`
    );
    if (!confirmed) return;

    await consolidateInterviewers(
      primaryInterviewer.id,
      legacyInterviewers.map((interviewer) => interviewer.id)
    );
  }, [
    consolidateInterviewers,
    isConsolidating,
    legacyInterviewers,
    primaryInterviewer,
  ]);

  if (!orgId) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Please select an organization to continue.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {primaryInterviewer && (
        <InterviewerCard
          interviewer={primaryInterviewer}
          onCopy={copyLink}
          title="Primary Training Workspace"
          subtitle="All new docs, interviews, and reingests now roll into this single training record."
        />
      )}

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">
            {primaryInterviewer ? 'Add Knowledge To Primary Training' : 'Set Up Primary Training'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {primaryInterviewer
              ? 'New sources update the same training workspace over time. Use this form when you want to add knowledge without opening the detail page first.'
              : 'Create the org’s primary training workspace. Future uploads and interviews will update this same record.'}
          </p>
          <div className="space-y-2">
            <Label htmlFor="session-name">Name</Label>
            <Input
              id="session-name"
              type="text"
              placeholder="e.g., Main product training"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isCreating || isUploadingFiles}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="website-url">Website URL</Label>
              <Input
                id="website-url"
                type="url"
                placeholder="https://example.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                disabled={isCreating || isUploadingFiles}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="knowledge-base-url">Knowledge Base URL</Label>
              <Input
                id="knowledge-base-url"
                type="url"
                placeholder="https://docs.example.com"
                value={knowledgeBaseUrl}
                onChange={(e) => setKnowledgeBaseUrl(e.target.value)}
                disabled={isCreating || isUploadingFiles}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="training-files">Upload Files</Label>
            <Input
              id="training-files"
              type="file"
              multiple
              accept=".pdf,video/*"
              onChange={(e) => setSelectedFiles(Array.from(e.target.files ?? []))}
              disabled={isCreating || isUploadingFiles}
            />
            {selectedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {selectedFiles.map((file) => (
                  <span
                    key={`${file.name}-${file.size}`}
                    className="rounded-md border bg-muted/50 px-2.5 py-1 text-xs"
                  >
                    {file.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Knowledge Assets */}
          <KnowledgeAssetSelector
            orgId={orgId}
            selectedAssetIds={selectedKnowledgeAssetIds}
            onSelectionChange={setSelectedKnowledgeAssetIds}
            disabled={isCreating || isUploadingFiles}
          />

          {/* Demo Assets */}
          <ContentSelector
            orgId={orgId}
            selectedVideoIds={selectedVideoIds}
            onSelectionChange={setSelectedVideoIds}
            disabled={isCreating || isUploadingFiles}
          />

          <Button
            onClick={handleCreate}
            disabled={isCreating || isUploadingFiles || !hasSources}
            className="w-full sm:w-auto"
          >
            {isCreating || isUploadingFiles ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              primaryInterviewer ? 'Update Primary Training' : 'Create Primary Training'
            )}
          </Button>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !primaryInterviewer ? (
        <div className="text-center py-12 text-muted-foreground">
          <GraduationCap className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No primary training yet. Create it above to get started.</p>
        </div>
      ) : legacyInterviewers.length > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-sm font-medium text-muted-foreground">
                Legacy Training Sessions ({legacyInterviewers.length})
              </h2>
              <p className="text-sm text-muted-foreground">
                These older records are kept for history, but new knowledge should flow into the primary training workspace above.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleConsolidateLegacy}
              disabled={isConsolidating}
            >
              {isConsolidating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Consolidate Legacy Training
            </Button>
          </div>
          {legacyInterviewers.map((interviewer) => (
            <InterviewerCard
              key={interviewer.id}
              interviewer={interviewer}
              onCopy={copyLink}
              onDelete={deleteInterviewer}
              isDeleting={isDeleting}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default InterviewerLinksContent;
